import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  Alert,
  LinearProgress,
  Avatar,
  Pagination,
  Breadcrumbs,
  Link,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Business as BusinessIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  LocationCity as LocationCityIcon,
  Business as MatrizIcon,
  Store as FilialIcon
} from '@mui/icons-material';

import { 
  escritorioService, 
  Escritorio, 
  EscritorioFilters 
} from '../services/escritorioService';
import EscritorioForm from '../components/forms/EscritorioForm';
import { detailPageHeaderPaperSx } from '../styles/theme';

const EscritoriosPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const primario = theme.palette.primary.main;
  
  // Estados principais
  const [escritorios, setEscritorios] = useState<Escritorio[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Estados de paginação
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });
  
  // Estados de filtros
  const [filters, setFilters] = useState<EscritorioFilters>({
    page: 1,
    limit: 10,
    tipo: 'todos',
    ativo: 'true'
  });
  
  // Estados do modal de criação/edição
  const [formModal, setFormModal] = useState({
    open: false,
    escritorio: null as Escritorio | null
  });
  
  // Estados do modal de exclusão
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    escritorio: null as Escritorio | null
  });
  
  // Estados de busca
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadEscritorios();
  }, [filters]);

  const loadEscritorios = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await escritorioService.getEscritorios(filters);
      
      if (response.success) {
        setEscritorios(response.data || []);
        if (response.pagination) {
          setPagination(response.pagination);
        }
      } else {
        throw new Error(response.message || 'Erro ao carregar escritórios');
      }
    } catch (err: any) {
      console.error('Erro ao carregar escritórios:', err);
      setError(err.message || 'Erro ao carregar dados dos escritórios');
      setEscritorios([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleFilterChange = (field: keyof EscritorioFilters, value: any) => {
    setFilters(prev => ({ 
      ...prev, 
      [field]: value,
      page: 1 // Reset para primeira página
    }));
  };

  const handleSearch = () => {
    if (searchTerm.trim()) {
      setFilters(prev => ({ 
        ...prev, 
        search: searchTerm.trim(),
        page: 1 
      }));
    } else {
      const { search, ...filtersWithoutSearch } = filters;
      setFilters({ ...filtersWithoutSearch, page: 1 });
    }
  };

  const handleOpenForm = (escritorio: Escritorio | null = null) => {
    setFormModal({ open: true, escritorio });
  };

  const handleCloseForm = () => {
    setFormModal({ open: false, escritorio: null });
  };

  const handleFormSuccess = () => {
    handleCloseForm();
    loadEscritorios();
  };

  const handleDeleteClick = (escritorio: Escritorio) => {
    if (escritorio.is_default) {
      setError('Escritório padrão não pode ser excluído');
      return;
    }
    setDeleteModal({ open: true, escritorio });
  };

  const confirmDelete = async () => {
    if (!deleteModal.escritorio) return;
    
    try {
      const response = await escritorioService.deleteEscritorio(deleteModal.escritorio.id);
      if (response.success) {
        setDeleteModal({ open: false, escritorio: null });
        loadEscritorios();
      } else {
        throw new Error(response.message || 'Erro ao excluir escritório');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao excluir escritório');
    }
  };

  const handleToggleStatus = async (escritorio: Escritorio) => {
    if (escritorio.is_default && escritorio.ativo) {
      setError('Escritório padrão não pode ser desativado');
      return;
    }
    
    try {
      const response = await escritorioService.toggleEscritorioStatus(escritorio.id);
      if (response.success) {
        loadEscritorios();
      } else {
        throw new Error(response.message || 'Erro ao alterar status');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao alterar status do escritório');
    }
  };

  const handleSetAsDefault = async (escritorio: Escritorio) => {
    if (!escritorio.ativo) {
      setError('Apenas escritórios ativos podem ser definidos como padrão');
      return;
    }
    
    try {
      const response = await escritorioService.setAsDefault(escritorio.id);
      if (response.success) {
        loadEscritorios();
      } else {
        throw new Error(response.message || 'Erro ao definir como padrão');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao definir escritório como padrão');
    }
  };

  const handleViewClients = (escritorio: Escritorio) => {
    // Navegar para a página de clientes com filtro por escritório
    navigate(`/clients?escritorio_id=${escritorio.id}&escritorio_nome=${encodeURIComponent(escritorio.nome_fantasia)}`);
  };

  const getTipoIcon = (tipo: string) => {
    switch (tipo) {
      case 'matriz':
        return <MatrizIcon fontSize="small" />;
      case 'filial':
        return <FilialIcon fontSize="small" />;
      default:
        return <BusinessIcon fontSize="small" />;
    }
  };

  const getTipoLabel = (tipo: string) => {
    switch (tipo) {
      case 'matriz':
        return 'Matriz';
      case 'filial':
        return 'Filial';
      default:
        return tipo;
    }
  };

  const getTipoColor = (tipo: string) => {
    switch (tipo) {
      case 'matriz':
        return 'primary';
      case 'filial':
        return 'secondary';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100%' }}>
      {/* Header */}
      <Box mb={1}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/dashboard')}
            sx={{ textDecoration: 'none' }}
          >
            Dashboard
          </Link>
          <Typography color="text.primary" variant="body2">
            Escritórios
          </Typography>
        </Breadcrumbs>

        <Paper elevation={0} sx={detailPageHeaderPaperSx(theme)}>
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Avatar sx={{ bgcolor: alpha(primario, 0.15), color: primario, width: 40, height: 40 }}>
                <BusinessIcon fontSize="small" />
              </Avatar>
              <Box>
                <Typography variant="h5" component="h1" fontWeight="bold">
                  Escritórios
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Gerencie os escritórios da empresa
                </Typography>
              </Box>
            </Box>

            <Box display="flex" gap={0.5}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<RefreshIcon fontSize="small" />}
                onClick={loadEscritorios}
                disabled={loading}
              >
                Atualizar
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon fontSize="small" />}
                onClick={() => handleOpenForm()}
              >
                Novo Escritório
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Filtros */}
      <Card sx={{ mb: 1, border: `1px solid ${theme.palette.divider}`, ...(theme.palette.mode === 'dark' && { backgroundImage: 'none' }) }}>
        <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
          <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
            Filtros
          </Typography>
          
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Buscar"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                sx={{ '& .MuiInputBase-root': { height: 32 } }}
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={handleSearch} size="small" sx={{ p: 0.25 }}>
                      <SearchIcon fontSize="small" />
                    </IconButton>
                  )
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.75rem' }}>Tipo</InputLabel>
                <Select
                  value={filters.tipo || 'todos'}
                  label="Tipo"
                  onChange={(e) => handleFilterChange('tipo', e.target.value)}
                  sx={{ '& .MuiSelect-select': { py: 0.5, fontSize: '0.875rem' } }}
                >
                  <MenuItem value="todos">Todos</MenuItem>
                  <MenuItem value="matriz">Matriz</MenuItem>
                  <MenuItem value="filial">Filial</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.75rem' }}>Situação</InputLabel>
                <Select
                  value={filters.ativo || 'true'}
                  label="Situação"
                  onChange={(e) => handleFilterChange('ativo', e.target.value)}
                  sx={{ '& .MuiSelect-select': { py: 0.5, fontSize: '0.875rem' } }}
                >
                  <MenuItem value="true">Ativo</MenuItem>
                  <MenuItem value="false">Inativo</MenuItem>
                  <MenuItem value="todos">Todos</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Cidade"
                value={filters.cidade || ''}
                onChange={(e) => handleFilterChange('cidade', e.target.value)}
                sx={{ '& .MuiInputBase-root': { height: 32 } }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Estado (UF)"
                value={filters.estado || ''}
                onChange={(e) => handleFilterChange('estado', e.target.value.toUpperCase())}
                sx={{ '& .MuiInputBase-root': { height: 32 } }}
                inputProps={{ maxLength: 2 }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Alerta de erro */}
      {error && (
        <Alert severity="error" sx={{ mb: 1, py: 0.5 }} onClose={() => setError(null)}>
          <Typography variant="body2">{error}</Typography>
        </Alert>
      )}

      {/* Progress bar */}
      {loading && <LinearProgress sx={{ mb: 1 }} />}

      {/* Tabela */}
      <Paper
        elevation={2}
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          border: `1px solid ${theme.palette.divider}`,
          ...(theme.palette.mode === 'dark' && {
            backgroundImage: 'none',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.28)',
          }),
        }}
      >
        <Box sx={{ px: 2, py: 1, bgcolor: alpha(primario, 0.04), borderBottom: `1px solid ${alpha(primario, 0.1)}` }}>
          <Typography variant="subtitle2" fontWeight={600}>
            Escritórios Cadastrados ({pagination.total})
          </Typography>
        </Box>
        <TableContainer sx={{ maxHeight: 520 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{
                '& .MuiTableCell-head': {
                  bgcolor: (t) => `color-mix(in srgb, ${primario} 12%, ${t.palette.background.paper})`,
                  fontWeight: 700,
                  fontSize: '0.73rem',
                  py: 0.8,
                  borderBottom: `2px solid ${alpha(primario, 0.2)}`,
                },
              }}>
                  <TableCell>Identificação</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Nome Fantasia</TableCell>
                  <TableCell>CNPJ</TableCell>
                  <TableCell>Cidade/UF</TableCell>
                  <TableCell align="center">Clientes</TableCell>
                  <TableCell>Situação</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 2 }}>
                      <Typography color="text.secondary" variant="body2">
                        Carregando escritórios...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : escritorios.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 2 }}>
                      <Typography color="text.secondary" variant="body2">
                        Nenhum escritório encontrado
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  escritorios.map((escritorio) => (
                    <TableRow key={escritorio.id} hover sx={{ '&:hover': { bgcolor: alpha(primario, 0.03) } }}>
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          {escritorio.is_default && (
                            <Tooltip title="Escritório Padrão">
                              <StarIcon color="warning" sx={{ fontSize: 16 }} />
                            </Tooltip>
                          )}
                          <Typography fontWeight="medium" variant="body2">
                            {escritorio.identificacao}
                          </Typography>
                        </Box>
                      </TableCell>
                      
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          {getTipoIcon(escritorio.tipo)}
                          <Chip
                            label={getTipoLabel(escritorio.tipo)}
                            color={getTipoColor(escritorio.tipo) as any}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.625rem' }}
                          />
                        </Box>
                      </TableCell>
                      
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                        <Typography variant="body2">
                          {escritorio.nome_fantasia}
                        </Typography>
                      </TableCell>
                      
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                        <Typography variant="caption">
                          {escritorio.cnpj}
                        </Typography>
                      </TableCell>
                      
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                        <Box display="flex" alignItems="center" gap={0.25}>
                          <LocationCityIcon sx={{ fontSize: 14 }} />
                          <Typography variant="caption">
                            {escritorio.cidade}/{escritorio.estado}
                          </Typography>
                        </Box>
                      </TableCell>
                      
                      <TableCell align="center" sx={{ py: 0.5 }}>
                        <Chip
                          label={escritorio.total_clientes_ativos || 0}
                          color="primary"
                          size="small"
                          variant="outlined"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewClients(escritorio);
                          }}
                          sx={{ 
                            height: 20, 
                            fontSize: '0.625rem',
                            minWidth: 30,
                            fontWeight: 600,
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: 'primary.light',
                              color: 'white'
                            }
                          }}
                        />
                      </TableCell>
                      
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                        <Chip
                          label={escritorio.ativo ? 'Ativo' : 'Inativo'}
                          color={escritorio.ativo ? 'success' : 'error'}
                          size="small"
                          variant="filled"
                          sx={{ height: 20, fontSize: '0.625rem' }}
                        />
                      </TableCell>
                      
                      <TableCell align="center" sx={{ py: 0.5 }}>
                        <Box display="flex" gap={0.25} justifyContent="center">
                          <Tooltip title="Ver detalhes">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenForm(escritorio)}
                              sx={{ p: 0.25 }}
                            >
                              <VisibilityIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="Editar">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenForm(escritorio)}
                              sx={{ p: 0.25 }}
                            >
                              <EditIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          
                          {!escritorio.is_default && (
                            <Tooltip title={escritorio.ativo ? 'Definir como padrão' : 'Ativar'}>
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => escritorio.ativo ? handleSetAsDefault(escritorio) : handleToggleStatus(escritorio)}
                                sx={{ p: 0.25 }}
                              >
                                <StarBorderIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          
                          {!escritorio.is_default && (
                            <Tooltip title={escritorio.ativo ? 'Desativar' : 'Ativar'}>
                              <IconButton
                                size="small"
                                color={escritorio.ativo ? 'error' : 'success'}
                                onClick={() => handleToggleStatus(escritorio)}
                                sx={{ p: 0.25 }}
                              >
                                {escritorio.ativo ? (
                                  <LockIcon sx={{ fontSize: 16 }} />
                                ) : (
                                  <LockOpenIcon sx={{ fontSize: 16 }} />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                          
                          {!escritorio.is_default && (
                            <Tooltip title="Excluir">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteClick(escritorio)}
                                sx={{ p: 0.25 }}
                              >
                                <DeleteIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
        </TableContainer>

        {/* Paginação */}
        {pagination.pages > 1 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 1.5,
              px: 2,
              bgcolor: alpha(primario, 0.04),
              borderTop: `2px solid ${alpha(primario, 0.2)}`,
            }}
          >
            <Pagination
              count={pagination.pages}
              page={pagination.page}
              onChange={handlePageChange}
              color="primary"
              size="small"
            />
          </Box>
        )}
      </Paper>

      {/* Modal de Formulário */}
      <Dialog
        open={formModal.open}
        onClose={handleCloseForm}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {formModal.escritorio ? 'Editar Escritório' : 'Novo Escritório'}
        </DialogTitle>
        <DialogContent>
          <EscritorioForm
            escritorio={formModal.escritorio}
            onSuccess={handleFormSuccess}
            onCancel={handleCloseForm}
          />
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmação de Exclusão */}
      <Dialog
        open={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, escritorio: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir o escritório "{deleteModal.escritorio?.identificacao}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteModal({ open: false, escritorio: null })}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDelete}
          >
            Confirmar Exclusão
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EscritoriosPage;
